import {
  PROVISIONING_STATUS_TO_STATUS_LABEL_MAP,
  ProvisioningStatus,
} from '../constants/provisioningStatus.ts';
import { useResetAtom } from 'jotai/utils';

import { FiltersChips } from 'common/components/Filters/FiltersChips.tsx';
import { useTranslation } from 'react-i18next';
import { SourceChips } from './Components/Chips/SourceChips.tsx';
import { isEmpty } from 'lodash';
import { createChipsContentText } from 'common/components/Chips/utils/chipsContentUtils.ts';
import {
  BulkProvisioningFilters,
  bulkProvisioningFiltersAtom,
} from 'atoms/filters/bulk-provisioning.ts';
import { useAtom } from 'jotai';

export const BulkFiltersChips = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useAtom(bulkProvisioningFiltersAtom);
  const resetFilters = useResetAtom(bulkProvisioningFiltersAtom);

  const createValuesForChips = (filters: BulkProvisioningFilters) => {
    const filtersNames = {
      status: t('common.status'),
      orderNumber: t('common.orderNumber'),
      source: t('bulkProvisioning.source'),
      uploadDate: t('common.uploadDate'),
    };

    const { status, orderNumber } = filters;
    const chipsValues: Partial<Record<keyof typeof filtersNames, string>> = {};

    if (status !== '') {
      chipsValues.status = createChipsContentText(
        filtersNames.status,
        PROVISIONING_STATUS_TO_STATUS_LABEL_MAP[Number(status) as ProvisioningStatus],
      );
    }

    if (orderNumber !== '') {
      chipsValues.orderNumber = createChipsContentText(filtersNames.orderNumber, orderNumber);
    }

    const from = filters.uploadDateFrom
      ? `from ${new Date(filters.uploadDateFrom).toLocaleDateString()}, `
      : '';

    const to = filters.uploadDateTo
      ? `to ${new Date(filters.uploadDateTo).toLocaleDateString()}`
      : '';

    if (from || to) {
      chipsValues.uploadDate = createChipsContentText(filtersNames.uploadDate, `${from}${to}`);
    }
    return chipsValues;
  };

  const onFilterClear = (filterName: string, value: string) => {
    if (filterName === 'uploadDate') {
      setFilters((currentFilters) => ({
        ...currentFilters,
        uploadDateFrom: null,
        uploadDateTo: null,
      }));
    } else if (filterName === 'source') {
      const newSourceFilters = filters.source.filter((source) => source !== +value);

      setFilters((currentFilters) => ({ ...currentFilters, [filterName]: newSourceFilters }));
    } else {
      setFilters((currentFilters) => ({ ...currentFilters, [filterName]: '' }));
    }
  };

  const filtersChips = createValuesForChips(filters);

  const externalChips = [];
  if (!isEmpty(filters.source)) {
    externalChips.push(
      <SourceChips
        source={filters.source}
        onDelete={(source) => {
          onFilterClear('source', source.toString());
        }}
      />,
    );
  }

  return (
    <FiltersChips
      externalChips={externalChips}
      filters={filtersChips}
      onFilterClear={onFilterClear}
      onFiltersClear={resetFilters}
    />
  );
};
